/**
 * USE ENTITY LIST
 * Hook générique pour gérer toute la logique commune des pages de liste
 */

import { useCallback, useEffect, useRef, useState } from 'react';
import { toast } from '../components/ui';
import { exportToCSV } from '../lib/csv';
import type { CSVImportConfig, CSVImportMode, CSVImportResult } from '../types/csv';
import type { FilterConfig } from '../types/filters';
import { useEnterpriseTable } from './useEnterpriseTable';

interface EntityListOptions<T> {
  // Données
  data: T[]; // Données affichées (paginées en mode server)
  allData?: T[]; // Toutes les données (pour Export/Import CSV en mode server)
  loading: boolean;
  error: Error | null;

  // CRUD operations
  create?: (data: any) => Promise<T | boolean | null>;
  update?: (id: string, data: any) => Promise<boolean>;
  remove?: (id: string) => Promise<boolean>;
  removeMany?: (ids: string[]) => Promise<boolean>;
  // Mappe une exception levée par remove/removeMany vers un message utilisateur.
  // Défaut : message générique (pas de fuite d'erreur SQL brute). Permet à une
  // entité dont la suppression porte des codes métier (ex: crew via RPC) de les
  // afficher proprement aussi depuis la liste (revue 2026-05-31, finding #6).
  removeErrorMessage?: (err: unknown) => string;
  refresh?: () => Promise<any[]>; // Retourne les données chargées

  // ✅ Export filtré (sans pagination)
  fetchFiltered?: (params: import('../types/crud').FetchFilteredParams) => Promise<T[]>;

  // Filtres obligatoires (onglet de statut, masquage expirés…) appliqués à
  // l'export CSV filtré. Sans eux, l'export global depuis un onglet exportait
  // TOUS les éléments (ticket #195, même bug que la sélection globale du 07/07).
  requiredFilters?: import('../types/filters').FilterCondition[];

  // Configuration
  entityName: string; // "produit", "contact", etc.
  entityNamePlural: string; // "produits", "contacts", etc.
  searchableFields: (keyof T)[];
  filterConfigs?: FilterConfig[];
  initialPageSize?: number;
  initialFilters?: import('../types/filters').FilterCondition[];
  initialColumnVisibility?: Record<string, boolean>; // Visibilité initiale des colonnes

  // Mode server-side (optionnel)
  mode?: 'client' | 'server';
  total?: number;
  totalPages?: number;
  currentPage?: number;
  itemsPerPage?: number;
  onSearchChange?: (search: string) => void;
  onPageChange?: (page: number) => void;
  onItemsPerPageChange?: (itemsPerPage: number) => void;
  onFiltersChange?: (filters: import('../types/filters').FilterCondition[]) => void;

  // CSV Export
  csvFileName?: string;
  csvTransform?: (item: T) => Record<string, any>;
  csvColumns?: Array<{ key: keyof T; label: string }>;
  csvTransformers?: Record<string, (value: any, item?: T) => string>;

  // CSV Import
  importConfig?: CSVImportConfig<T>;
  onImportSuccess?: (result: CSVImportResult<T>) => void;

  // Callbacks optionnels
  onCreateSuccess?: (item: T | boolean | null) => void;
  onUpdateSuccess?: () => void;
  onDeleteSuccess?: () => void;
}

export function useEntityList<T extends { id: string }>(options: EntityListOptions<T>) {
  const {
    data,
    allData: providedAllData,
    loading,
    error,
    create,
    update: _update,
    remove,
    removeMany,
    removeErrorMessage,
    refresh,
    fetchFiltered, // ✅ Nouvelle fonction pour export filtré
    requiredFilters,
    entityName,
    entityNamePlural,
    searchableFields,
    filterConfigs = [],
    initialPageSize = 25,
    initialFilters = [],
    initialColumnVisibility = {},
    csvFileName,
    csvTransform,
    csvColumns,
    csvTransformers,
    importConfig: _importConfig,
    onCreateSuccess,
    onImportSuccess,
    onDeleteSuccess,
    mode,
    total,
    totalPages,
    currentPage,
    itemsPerPage,
    onSearchChange,
    onPageChange,
    onItemsPerPageChange,
    onFiltersChange,
  } = options;

  // En mode server, utiliser providedAllData pour export/import, sinon data
  const allData = providedAllData || data;

  // =========================================================================
  // STATES
  // =========================================================================

  const [isColumnModalOpen, setIsColumnModalOpen] = useState(false);
  const [isFiltersModalOpen, setIsFiltersModalOpen] = useState(false);
  const [isCreateModalOpen, setIsCreateModalOpen] = useState(false);
  const [isImportModalOpen, setIsImportModalOpen] = useState(false);
  const [importMode, setImportMode] = useState<CSVImportMode>({ mode: 'create' });
  const [deleteConfirm, setDeleteConfirm] = useState<{
    open: boolean;
    id?: string;
    name?: string;
    isBulk?: boolean;
  }>({ open: false });

  // Fonction pour ouvrir la modal d'import (avec chargement des données en mode server)
  const openImportModal = useCallback(
    async (newImportMode: CSVImportMode) => {
      // En mode server, charger toutes les données d'abord pour vérifier doublons
      if (mode === 'server' && refresh) {
        toast.info(`Chargement de tous les ${entityNamePlural} pour vérification...`);
        await refresh();
      }

      setImportMode(newImportMode);
      setIsImportModalOpen(true);
    },
    [mode, refresh, entityNamePlural]
  );

  // =========================================================================
  // TABLE
  // =========================================================================

  const table = useEnterpriseTable<T>({
    data,
    searchableFields,
    initialPageSize,
    initialFilters,
    initialColumnVisibility,
    mode: mode || 'client',
    total,
    totalPages,
    currentPage,
    itemsPerPage,
    onSearchChange,
    onPageChange,
    onItemsPerPageChange,
    onFiltersChange,
  });

  // =========================================================================
  // HANDLERS - CREATE
  // =========================================================================

  const handleCreate = useCallback(
    async (input: any) => {
      if (!create) return;

      try {
        const result = await create(input);
        if (result) {
          toast.success(
            `${entityName.charAt(0).toUpperCase() + entityName.slice(1)} créé(e) avec succès`
          );
          setIsCreateModalOpen(false);

          // En mode server, rafraîchir les données pour voir le nouvel élément
          if (mode === 'server' && refresh) {
            await refresh();
          }

          if (onCreateSuccess) onCreateSuccess(result);
          return true;
        } else {
          toast.error(`Erreur lors de la création du/de la ${entityName}`);
          return false;
        }
      } catch {
        toast.error(`Erreur lors de la création du/de la ${entityName}`);
        return false;
      }
    },
    [create, entityName, onCreateSuccess, mode, refresh]
  );

  // =========================================================================
  // HANDLERS - DELETE
  // =========================================================================

  const handleDeleteClick = useCallback((id: string, name?: string) => {
    setDeleteConfirm({ open: true, id, name, isBulk: false });
  }, []);

  const handleBulkDeleteClick = useCallback(() => {
    if (table.selectedIds.size === 0) return;
    setDeleteConfirm({ open: true, isBulk: true });
  }, [table.selectedIds.size]);

  const confirmDelete = useCallback(async () => {
    try {
      if (deleteConfirm.isBulk && removeMany) {
        // ✅ Utiliser selectedIds (global) au lieu de selectedItems (page courante uniquement)
        const ids = Array.from(table.selectedIds);
        const success = await removeMany(ids);

        if (success) {
          toast.success(`${ids.length} ${entityNamePlural} supprimé(e)s`);
          table.clearSelection();

          // En mode server, rafraîchir les données
          if (mode === 'server' && refresh) {
            await refresh();
          }

          if (onDeleteSuccess) onDeleteSuccess();
        } else {
          toast.error('Erreur lors de la suppression');
        }
      } else if (deleteConfirm.id && remove) {
        const success = await remove(deleteConfirm.id);

        if (success) {
          toast.success(`${entityName.charAt(0).toUpperCase() + entityName.slice(1)} supprimé(e)`);

          // En mode server, rafraîchir les données
          if (mode === 'server' && refresh) {
            await refresh();
          }

          if (onDeleteSuccess) onDeleteSuccess();
        } else {
          toast.error('Erreur lors de la suppression');
        }
      }
    } catch (err) {
      toast.error(removeErrorMessage ? removeErrorMessage(err) : 'Erreur lors de la suppression');
    } finally {
      setDeleteConfirm({ open: false });
    }
  }, [
    deleteConfirm,
    remove,
    removeMany,
    removeErrorMessage,
    table,
    entityName,
    entityNamePlural,
    onDeleteSuccess,
    mode,
    refresh,
  ]);

  // =========================================================================
  // HANDLERS - CSV
  // =========================================================================

  const handleExportCSV = useCallback(async () => {
    let dataSource = allData;

    // En mode server, utiliser fetchFiltered pour récupérer les données filtrées
    if (mode === 'server' && fetchFiltered) {
      toast.info(`Chargement des ${entityNamePlural} filtrés pour export...`);
      // ✅ Utiliser fetchFiltered avec les filtres actuels
      const filteredData = await fetchFiltered({
        search: table.searchTerm,
        filters: table.activeFilters,
        filterMode: table.filterMode,
        requiredFilters,
      });
      if (filteredData) {
        // Ticket #195 : 0 résultat = export vide légitime (onglet/filtres sans
        // lignes). L'ancien test `length > 0` retombait sur allData et pouvait
        // exporter TOUTE la base si un refresh l'avait peuplée entre-temps.
        if (filteredData.length === 0) {
          toast.info(`Aucun ${entityName} à exporter avec ces filtres`);
          return;
        }
        dataSource = filteredData as any;
      }
    } else if (mode === 'server' && refresh) {
      // Fallback si fetchFiltered n'est pas disponible
      toast.info(`Chargement de tous les ${entityNamePlural} pour export...`);
      const freshData = await refresh();
      if (freshData && freshData.length > 0) {
        dataSource = freshData as any;
      }
    }

    // ✅ Exporter TOUTES les colonnes définies dans csvColumns (pas de filtrage par visibilité)
    // Les clients veulent toujours le maximum de colonnes dans l'export
    const allColumns = csvColumns ? csvColumns.map((col) => String(col.key)) : undefined;

    // Utiliser la source de données appropriée
    const dataToExport = csvTransform ? dataSource.map(csvTransform) : dataSource;

    const fileName = csvFileName || `${entityNamePlural}.csv`;
    exportToCSV(dataToExport as any, fileName, {
      columns: csvColumns as any,
      visibleColumns: allColumns, // Toutes les colonnes exportables
      transformers: csvTransformers,
    });
    toast.success(`Export CSV de ${dataToExport.length} ${entityNamePlural} réussi`);
  }, [
    mode,
    fetchFiltered,
    requiredFilters,
    refresh,
    allData,
    table.searchTerm,
    table.activeFilters,
    table.filterMode,
    csvTransform,
    csvColumns,
    csvTransformers,
    csvFileName,
    entityName,
    entityNamePlural,
  ]);

  const handleExportSelectedCSV = useCallback(async () => {
    if (table.selectedIds.size === 0) {
      toast.error('Aucun élément sélectionné');
      return;
    }

    // ✅ Récupérer les données sélectionnées
    let selectedData: T[];
    const selectedIds = Array.from(table.selectedIds);

    if (mode === 'server' && fetchFiltered) {
      // En mode server, récupérer les données via fetchFiltered avec les IDs
      toast.info(`Préparation de l'export de ${selectedIds.length} ${entityNamePlural}...`);
      try {
        selectedData = await fetchFiltered({ ids: selectedIds });
      } catch (err) {
        // Bug fix : le wrapper fetchInChunks expose le contexte (chunk N/M, cause)
        // → on l'expose à l'utilisateur au lieu d'un message générique masquant.
        const message = err instanceof Error ? err.message : String(err);
        console.error('[handleExportSelectedCSV] fetchFiltered failed:', err);
        toast.error(`Export échoué : ${message}`);
        return;
      }
    } else {
      // En mode client, filtrer allData
      selectedData = allData.filter((item) => table.selectedIds.has(item.id));
    }

    // Bug fix : selectedIds.size > 0 mais 0 résultat = fail technique (DB sync,
    // RLS qui filtre tout, IDs invalides), pas un cas legitime "rien à exporter".
    // Ne plus afficher le toast trompeur "Aucune donnée à exporter".
    if (selectedData.length === 0) {
      console.error(
        `[handleExportSelectedCSV] 0 résultats pour ${selectedIds.length} IDs sélectionnés (mode=${mode})`
      );
      toast.error(
        `Export échoué : 0 ${entityNamePlural} récupérés sur ${selectedIds.length} sélectionnés. Réessayez ou rechargez la page.`
      );
      return;
    }

    // Avertir si tous les éléments n'ont pas été trouvés
    if (selectedData.length < selectedIds.length) {
      toast.warning(
        `Export partiel: ${selectedData.length}/${selectedIds.length} éléments trouvés`
      );
    }

    // ✅ Exporter TOUTES les colonnes définies dans csvColumns (pas de filtrage par visibilité)
    // Les clients veulent toujours le maximum de colonnes dans l'export
    const allColumns = csvColumns ? csvColumns.map((col) => String(col.key)) : undefined;

    const dataToExport = csvTransform ? selectedData.map(csvTransform) : selectedData;

    const fileName = csvFileName || `${entityNamePlural}-selection.csv`;
    try {
      exportToCSV(dataToExport as any, fileName, {
        columns: csvColumns as any,
        visibleColumns: allColumns, // Toutes les colonnes exportables
        transformers: csvTransformers,
        silentEmpty: true, // contrôle le messaging ici (déjà géré ci-dessus)
      });
      toast.success(`Export CSV de ${selectedData.length} ${entityNamePlural} réussi`);
    } catch (err) {
      const message = err instanceof Error ? err.message : String(err);
      console.error('[handleExportSelectedCSV] exportToCSV failed:', err);
      toast.error(`Export échoué (génération CSV) : ${message}`);
    }
  }, [
    table.selectedIds,
    allData,
    mode,
    fetchFiltered,
    csvTransform,
    csvColumns,
    csvTransformers,
    csvFileName,
    entityNamePlural,
  ]);

  // =========================================================================
  // HANDLERS - IMPORT CSV
  // =========================================================================

  const handleImportSuccess = useCallback(
    (result: CSVImportResult<T>) => {
      const total = result.created + result.updated + result.errors.length;

      // Succès complet
      if (result.errors.length === 0) {
        if (result.created > 0 && result.updated === 0) {
          toast.success(`${result.created} ${entityNamePlural} créés avec succès`);
        } else if (result.updated > 0 && result.created === 0) {
          toast.success(`${result.updated} ${entityNamePlural} mis à jour avec succès`);
        } else if (result.created > 0 && result.updated > 0) {
          toast.success(`Import réussi : ${result.created} créés, ${result.updated} mis à jour`);
        }
      }
      // Succès partiel avec erreurs
      else if (result.created > 0 || result.updated > 0) {
        toast.warning(
          `⚠️ Import partiel : ${result.created} créés, ${result.updated} mis à jour, ${result.errors.length} erreurs sur ${total} lignes`,
          { duration: 5000 }
        );
      }
      // Échec complet
      else {
        toast.error(
          `Import échoué : ${result.errors.length} erreurs sur ${total} lignes. Consultez le rapport d'erreurs.`,
          { duration: 6000 }
        );
      }

      // NE PAS rafraîchir ici - ça cause une race condition avec le modal encore ouvert
      // Le refresh sera fait automatiquement quand l'utilisateur ferme le modal
      // via le useEffect ci-dessous

      if (onImportSuccess) {
        onImportSuccess(result);
      }

      // Ne pas fermer automatiquement le modal - laisser l'utilisateur consulter le rapport
      // Le modal se fermera quand il cliquera sur "Fermer"
    },
    [entityNamePlural, onImportSuccess]
  );

  // =========================================================================
  // EFFECTS
  // =========================================================================

  // Rafraîchir les données quand le modal d'import se ferme
  const prevImportModalOpen = useRef(false);

  useEffect(() => {
    // Si le modal vient de se fermer (transition true → false)
    if (prevImportModalOpen.current === true && isImportModalOpen === false && refresh) {
      refresh().catch((err) => {
        console.error('Erreur lors du rafraîchissement:', err);
      });
    }

    // Mettre à jour la ref APRÈS la détection (à la fin)
    prevImportModalOpen.current = isImportModalOpen;
  }, [isImportModalOpen, refresh]);

  // =========================================================================
  // RETURN
  // =========================================================================

  return {
    // States
    loading,
    error,
    isColumnModalOpen,
    isFiltersModalOpen,
    isCreateModalOpen,
    isImportModalOpen,
    importMode,
    deleteConfirm,
    isServerMode: mode === 'server', // Exposer le mode

    // Setters
    setIsColumnModalOpen,
    setIsFiltersModalOpen,
    setIsCreateModalOpen,
    setIsImportModalOpen,
    openImportModal,
    setImportMode,
    setDeleteConfirm,

    // Table
    table,
    filterConfigs,

    // Handlers
    handleCreate,
    handleDeleteClick,
    handleBulkDeleteClick,
    confirmDelete,
    handleExportCSV,
    handleExportSelectedCSV,
    handleImportSuccess,

    // Data (pour les actions bulk personnalisées)
    allData,
  };
}
